/**
 * Trade GP and venue margin helpers for Pricing Manager (NZ).
 * Retail/pour prices are inc GST; unit cost from Matrix is ex GST (grossed up × 1.15).
 * All selling columns (including Venue/L) use trade GP so Guide target = grid %.
 */

export const GST_MULT = 1.15;

/** NZ selling-price columns that show GP% (AU columns excluded). */
export const SELLING_PRICE_FIELDS_WITH_MARGIN = new Set([
  'rrpUnit',
  'rrpPack',
  'rrpCase',
  'priceVenuePerLitre',
  'pricePint',
  'priceHalfPint',
  'price150ml',
  'price500ml',
]);

/** Maps ProposalEditor camelCase keys to DB snake_case for margin target API */
export const CAMEL_TO_SNAKE_PRICE = {
  rrpUnit: 'rrp_unit',
  rrpPack: 'rrp_pack',
  rrpCase: 'rrp_case',
  priceVenuePerLitre: 'price_venue_per_litre',
  pricePint: 'price_pint',
  priceHalfPint: 'price_half_pint',
  price150ml: 'price_150ml',
  price500ml: 'price_500ml',
};

/** Allowed price_field values for margin targets (matches backend MARGIN_TARGET_PRICE_FIELDS) */
export const MARGIN_TARGET_PRICE_FIELDS = [
  'rrp_unit',
  'rrp_pack',
  'rrp_case',
  'price_venue_per_litre',
  'price_pint',
  'price_half_pint',
  'price_150ml',
  'price_500ml',
];

/** Cin7 product ranges allowed when setting margin targets (AdditionalAttribute3). */
export const MARGIN_TARGET_PRODUCT_RANGES = ['Core', 'Rebrew', 'NPD'];

/** Guide matrix rows: wildcard default then Cin7 ranges. */
export const MARGIN_TARGET_DEFAULT_RANGE = '*';

export const MARGIN_TARGET_GUIDE_RANGES = [
  { value: MARGIN_TARGET_DEFAULT_RANGE, label: 'Default' },
  { value: 'Core', label: 'Core' },
  { value: 'Rebrew', label: 'Rebrew' },
  { value: 'NPD', label: 'NPD' },
];

export const MARGIN_TARGET_FIELD_LABELS = {
  rrp_unit: 'RRP Unit',
  rrp_pack: 'RRP Pack',
  rrp_case: 'RRP Case',
  price_venue_per_litre: 'Venue/L',
  price_pint: 'Pint (440ml)',
  price_half_pint: 'Half Pint (285ml)',
  price_150ml: '150ml Pour',
  price_500ml: '500ml Pour',
};

export function calculateTradeGP(retailIncGst, unitCostExGst) {
  if (retailIncGst <= 0 || unitCostExGst == null || Number.isNaN(unitCostExGst)) return null;
  const costIncGst = unitCostExGst * GST_MULT;
  const gp = ((retailIncGst - costIncGst) / retailIncGst) * 100;
  if (!Number.isFinite(gp)) return null;
  return Math.round(gp * 10) / 10;
}

export function kegVolumeMlFromSku(sku) {
  if (!sku) return 50000;
  const ct = String(sku.containerType ?? sku.container_type ?? '').toLowerCase();
  if (ct !== 'keg') return 50000;
  const ut = parseInt(String(sku.unitType ?? sku.unit_type ?? ''), 10);
  if (!Number.isNaN(ut) && ut >= 10000) return ut;
  return 50000;
}

/**
 * Parses case_type like "24x1" or "6x4" → outer pack count, units per pack, total cans in case.
 */
export function parseCaseUnits(caseTypeStr) {
  if (!caseTypeStr || typeof caseTypeStr !== 'string') return null;
  const parts = caseTypeStr.trim().split('x').map((s) => parseInt(s, 10));
  if (parts.length !== 2 || parts.some((n) => Number.isNaN(n)) || parts[0] <= 0 || parts[1] <= 0) {
    return null;
  }
  const [casePacks, unitsPerPack] = parts;
  return { casePacks, unitsPerPack, totalUnits: casePacks * unitsPerPack };
}

function parsePrice(v) {
  if (v == null || v === '') return NaN;
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? NaN : n;
}

/**
 * Unit cost ex GST implied for a selling column, from trade prices on the same row.
 */
export function getUnitCostExGstForField(sku, fieldKey, prices) {
  const priceOff = parsePrice(prices.priceOff);
  const priceOn = parsePrice(prices.priceOn);
  const ct = String(sku?.containerType ?? sku?.container_type ?? '').toLowerCase();
  const isKeg = ct === 'keg';
  const caseType = sku?.caseType ?? sku?.case_type;

  if (fieldKey === 'rrpUnit') {
    if (Number.isNaN(priceOff)) return null;
    const parsed = parseCaseUnits(caseType);
    if (!parsed || parsed.totalUnits <= 0) return null;
    return priceOff / parsed.totalUnits;
  }
  if (fieldKey === 'rrpPack') {
    if (Number.isNaN(priceOff)) return null;
    const parsed = parseCaseUnits(caseType);
    if (!parsed || parsed.casePacks <= 0) return null;
    return priceOff / parsed.casePacks;
  }
  if (fieldKey === 'rrpCase') {
    if (Number.isNaN(priceOff)) return null;
    return priceOff;
  }
  if (fieldKey === 'priceVenuePerLitre') {
    if (!isKeg || Number.isNaN(priceOn)) return null;
    const ml = kegVolumeMlFromSku(sku);
    const kegL = ml / 1000;
    if (kegL <= 0) return null;
    return priceOn / kegL;
  }
  if (fieldKey === 'pricePint') {
    if (!isKeg || Number.isNaN(priceOn)) return null;
    const ml = kegVolumeMlFromSku(sku);
    const kegL = ml / 1000;
    if (kegL <= 0) return null;
    const costPerL = priceOn / kegL;
    return costPerL * 0.440;
  }
  if (fieldKey === 'priceHalfPint') {
    if (!isKeg || Number.isNaN(priceOn)) return null;
    const ml = kegVolumeMlFromSku(sku);
    const kegL = ml / 1000;
    if (kegL <= 0) return null;
    const costPerL = priceOn / kegL;
    return costPerL * 0.285;
  }
  if (fieldKey === 'price150ml') {
    if (!isKeg || Number.isNaN(priceOn)) return null;
    const ml = kegVolumeMlFromSku(sku);
    const kegL = ml / 1000;
    if (kegL <= 0) return null;
    const costPerL = priceOn / kegL;
    return costPerL * 0.150;
  }
  if (fieldKey === 'price500ml') {
    if (!isKeg || Number.isNaN(priceOn)) return null;
    const ml = kegVolumeMlFromSku(sku);
    const kegL = ml / 1000;
    if (kegL <= 0) return null;
    const costPerL = priceOn / kegL;
    return costPerL * 0.500;
  }
  return null;
}

/**
 * @returns {{ displayPct: number|null, isSimple: boolean }}
 */
export function computeMargin(fieldKey, sku, prices) {
  if (!SELLING_PRICE_FIELDS_WITH_MARGIN.has(fieldKey)) {
    return { displayPct: null, isSimple: false };
  }
  const retail = parsePrice(prices[fieldKey]);
  if (Number.isNaN(retail) || retail <= 0) {
    return { displayPct: null, isSimple: false };
  }
  const cost = getUnitCostExGstForField(sku, fieldKey, prices);
  if (cost == null || Number.isNaN(cost)) {
    return { displayPct: null, isSimple: false };
  }
  const gp = calculateTradeGP(retail, cost);
  return { displayPct: gp, isSimple: false };
}

/**
 * Merge current snapshot, saved proposed prices, and in-progress edits for margin math.
 */
export function getEffectivePrices(item, editedForItem) {
  const merged = {
    ...(item.currentPrices || {}),
    ...(item.proposedPrices || {}),
  };
  if (!editedForItem) return merged;
  Object.keys(editedForItem).forEach((k) => {
    const v = editedForItem[k];
    if (v != null && v !== '') {
      merged[k] = v;
    }
  });
  return merged;
}

function entryPct(entry) {
  if (entry == null) return null;
  if (typeof entry === 'number') return Number.isNaN(entry) ? null : entry;
  const { pct } = entry;
  return pct != null && !Number.isNaN(pct) ? pct : null;
}

/**
 * Build lookup map: "Range:snake_field:condition" -> { pct, rounding, id }
 * Also sets "Range:snake_field" for base (empty condition) so old callers work.
 */
export function buildTargetMap(targetRows) {
  const map = {};
  (targetRows || []).forEach((row) => {
    const r = row.productRange ?? row.product_range ?? row.range;
    const f = row.priceField ?? row.price_field;
    const pct = parseFloat(row.targetGpPct ?? row.target_gp_pct);
    const condition = String(row.condition ?? '');
    const rounding = row.rounding || 'nearest_50';
    if (r == null || f == null || Number.isNaN(pct)) return;
    const entry = {
      pct, rounding, condition, id: row.id,
    };
    map[`${String(r).trim()}:${String(f)}:${condition}`] = entry;
    if (!condition) {
      map[`${String(r).trim()}:${String(f)}`] = entry;
    }
  });
  return map;
}

export function resolveTargetEntry(targetMap, productRange, camelField, condition = '') {
  const snake = CAMEL_TO_SNAKE_PRICE[camelField];
  if (!snake || !targetMap) return null;
  const r = productRange != null ? String(productRange).trim() : '';
  const cond = condition || '';
  const keys = [
    `${r}:${snake}:${cond}`,
    `*:${snake}:${cond}`,
    `${r}:${snake}:`,
    `*:${snake}:`,
    `${r}:${snake}`,
    `*:${snake}`,
  ];
  for (let i = 0; i < keys.length; i += 1) {
    const entry = targetMap[keys[i]];
    if (entry != null) return typeof entry === 'number' ? { pct: entry, rounding: 'nearest_50' } : entry;
  }
  return null;
}

export function resolveTargetPct(targetMap, productRange, camelField, condition = '') {
  return entryPct(resolveTargetEntry(targetMap, productRange, camelField, condition));
}

/**
 * Whether the displayed margin meets the stored target for that price field.
 * `actualPct` must come from `computeMargin` for the same field so it matches target semantics.
 * @returns {boolean|null} null if either side is missing/invalid
 */
export function marginMeetsTarget(actualPct, targetPct) {
  if (actualPct == null || targetPct == null || Number.isNaN(targetPct)) return null;
  return actualPct >= targetPct;
}
